import dayjs from 'dayjs';
import { AccessGrant, Schedule, VisitorGroup, WeekSchedule } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { getAllAccessGrantsOfVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import { EMPTY_VALUE_PLACEHOLDER } from 'v2/constants';

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const CONSTANT_DATE = '2000-01-01';

const getDayIndex = (dayName: string) => DAY_ORDER.indexOf(dayName.toLowerCase());

const formatTime = (time: string) => dayjs(`${CONSTANT_DATE} ${time}`).format('HH:mm');

const getDayAbbr = (dayName: string, translation: Translation) => {
  switch (dayName.toLowerCase()) {
    case 'monday':
      return translation.mondayAbbr;
    case 'tuesday':
      return translation.tuesdayAbbr;
    case 'wednesday':
      return translation.wednesdayAbbr;
    case 'thursday':
      return translation.thursdayAbbr;
    case 'friday':
      return translation.fridayAbbr;
    case 'saturday':
      return translation.saturdayAbbr;
    case 'sunday':
      return translation.sundayAbbr;
    default:
      return dayName.slice(0, 3);
  }
};

const scheduledDays = (accessGrants: AccessGrant[]) => {
  const dayMap = new Map<string, { from: string; to: string }>();
  accessGrants.forEach((grant) => {
    grant.accessSchedule.weekDays.forEach((day) => {
      const key = day.dayName.toLowerCase();
      if (!dayMap.has(key)) {
        dayMap.set(key, { from: day.from, to: day.to });
      }
    });
  });

  const sortedDays = Array.from(dayMap.entries())
    .sort(([a], [b]) => getDayIndex(a) - getDayIndex(b))
    .map(([dayName, times]) => ({ dayName, ...times }));

  return sortedDays;
};

export interface ScheduleLine {
  day: string;
  time: string;
}

/**
 * Returns the first weekday's time range of a WeekSchedule (used for office mode display),
 * or '-' if no days are configured.
 * Example: '09:00 - 17:00'
 */
export const getOfficeModeDisplay = (officeMode: Schedule | WeekSchedule | null | undefined): string => {
  const weekDays = officeMode?.weekDays;
  if (weekDays && weekDays.length > 0) {
    const first = weekDays[0];
    return `${first.from.slice(0, -3)} - ${first.to.slice(0, -3)}`;
  }
  return EMPTY_VALUE_PLACEHOLDER;
};

/**
 * Returns one entry per consecutive group of days that share the same time range.
 * Example: [{ day: 'MON-WED', time: '09:00 - 17:00' }, { day: 'THU', time: '10:00 - 16:00' }]
 */
export const getScheduleLines = (visitorGroup: VisitorGroup, translation: Translation): ScheduleLine[] => {
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);
  const sortedDays = scheduledDays(accessGrants);

  if (sortedDays.length === 0) return [];

  const lines: ScheduleLine[] = [];
  let groupStart = 0;

  for (let i = 1; i <= sortedDays.length; i++) {
    const current = sortedDays[i - 1];
    const next = sortedDays[i];

    const isConsecutive =
      next !== undefined &&
      getDayIndex(next.dayName) === getDayIndex(current.dayName) + 1 &&
      next.from === current.from &&
      next.to === current.to;

    if (!isConsecutive) {
      const start = sortedDays[groupStart];
      const day =
        groupStart === i - 1
          ? getDayAbbr(start.dayName, translation)
          : `${getDayAbbr(start.dayName, translation)}-${getDayAbbr(current.dayName, translation)}`;

      lines.push({ day, time: `${formatTime(start.from)} - ${formatTime(start.to)}` });
      groupStart = i;
    }
  }

  return lines;
};
